import { Check, FileText } from 'lucide-react'
/**
 * Applicant Consent card — landlord-facing confirmation that the tenant
 * accepted Terms of Service + Credit Check Authorization (Figma Applicant
 * Consent). Shown for EVERY application type (standard, student housing,
 * guarantor; pre-screen and full). Timestamp is the consent form's own
 * `consented_at` (2026-09-07 — it used to show the Review submit time, which
 * can be days later), falling back to the application's creation time for
 * legacy rows without a consent row. Rendered in America/Toronto like every
 * other date, as "21 July, 2026, 04:00 PM" (client 2026-09-09).
 */
import type { ReactNode } from 'react'
import { useFormatDate } from '#app/utils/application-detail-v2/vendor/use-format-date'
import { cn } from '#app/utils/misc'
import { DETAIL_CARD, IconSquare } from '../primitives'

/**
 * The consent rows alone — the individual card's body and the combined
 * report's per-member accordion body (2026-09-08) render the SAME rows.
 */
export function ApplicantConsentBody({
	orgTerms,
	fallbackTermsHref,
	className,
}: {
	orgTerms?: { fileName: string | null; blobUrl: string } | null
	/** Normal-report placeholder until the landlord uploads custom terms. */
	fallbackTermsHref?: string
	className?: string
}) {
	const orgTermsHref = orgTerms
		? `/resources/view-blob?url=${encodeURIComponent(orgTerms.blobUrl)}`
		: fallbackTermsHref
	return (
		<div className={cn('space-y-4', className)}>
			<ConsentRow
				icon={<FileText className="size-4 text-[#747474]" />}
				label={
					<>
						I agree to the{' '}
						<a
							href="/terms"
							target="_blank"
							rel="noreferrer"
							className="font-semibold underline underline-offset-2"
						>
							Terms of Service
						</a>
					</>
				}
			/>
			<ConsentRow
				icon={
					<img
						src="/img/credit-check-icon.svg"
						alt=""
						className="size-4 shrink-0"
					/>
				}
				label={
					<a
						href="/terms"
						target="_blank"
						rel="noreferrer"
						className="font-semibold underline underline-offset-2"
					>
						Credit Check Authorization
					</a>
				}
			/>
			{orgTermsHref ? (
				<ConsentRow
					icon={<FileText className="size-4 text-[#747474]" />}
					label={
						<a
							href={orgTermsHref}
							target="_blank"
							rel="noreferrer"
							className="font-semibold underline underline-offset-2"
						>
							Landlord Terms & Conditions
							{orgTerms?.fileName?.trim()
								? ` — ${orgTerms.fileName.trim()}`
								: ''}
						</a>
					}
				/>
			) : null}
		</div>
	)
}

export function ApplicantConsent({
	consentedAt,
	orgTerms,
}: {
	consentedAt?: string | null
	/** The landlord org's own Terms & Conditions from the frozen Application
	 * Form config (TT-65) — a third consent row linking the uploaded PDF via
	 * the blob proxy. null/absent when the template's terms section is off. */
	orgTerms?: { fileName: string | null; blobUrl: string } | null
}) {
	// "21 July, 2026, 04:00 PM" (client 2026-09-09) — the pipeline-card
	// format, not the default "July 21, 2026 at 4:00 p.m." style.
	const { formatPipelineDateTime } = useFormatDate()
	const when = consentedAt ? formatPipelineDateTime(consentedAt) : null

	return (
		<section className={cn(DETAIL_CARD, 'p-6 sm:p-7')}>
			<div className="flex flex-wrap items-center gap-3">
				<IconSquare tone="green">
					<span
						aria-hidden="true"
						className="size-5 bg-[#A5BB83] [mask-image:url('/img/applicant-consent-icon.svg')] [mask-position:center] [mask-repeat:no-repeat] [mask-size:contain]"
					/>
				</IconSquare>
				<h2 className="text-[16px] font-semibold text-[#32302F]">
					Applicant Consent
				</h2>
				{when && when !== '-' ? (
					<span className="text-[14px] text-[#747474]">({when})</span>
				) : null}
			</div>

			<ApplicantConsentBody
				orgTerms={orgTerms}
				fallbackTermsHref="/demo-landlord-terms.pdf"
				className="mt-5"
			/>
		</section>
	)
}

function ConsentRow({
	icon,
	label,
}: {
	icon: ReactNode
	label: ReactNode
}) {
	return (
		<div className="flex items-center gap-3">
			<span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#16A354]">
				<Check className="size-3.5 text-white" strokeWidth={3} />
			</span>
			<span className="flex items-center gap-2 text-[15px] text-[#32302F]">
				{icon}
				{label}
			</span>
		</div>
	)
}
